import { AccountOutreachTodoCard } from 'components/dashboard/AccountOutreach'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CloseIcon,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  MuiDialogTitle,
  SlidersHIcon,
  SpinnerButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  makeStyles,
  useModal,
} from '@forgeglobal/design-framework'
import { ClaimAccountTodoCard } from 'components/dashboard/ClaimAccount'
import { StyleDefaults } from 'isolated-design-library'
import { TodoFragment, TodoItemStatusType, useAllTodosQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment, useState } from 'react'

const useStyles = makeStyles(theme => ({
  defaultButton: {
    height: theme.spacing(4),
    textTransform: 'none',
    color: StyleDefaults.palette.blue.dark,
    backgroundColor: StyleDefaults.palette.surface.main,
    borderColor: StyleDefaults.palette.surface.dark,
    borderLeftColor: `${StyleDefaults.palette.surface.dark} !important`,
    flexGrow: 1,
  },
  borderRadius: {
    borderRadius: theme.spacing(0.5),
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  selectedButton: {
    backgroundColor: `${theme.palette.common.white} !important`,
    color: `${theme.palette.secondary.light} !important`,
    borderColor: StyleDefaults.palette.surface.dark,
  },
  infoIcon: {
    marginRight: theme.spacing(2),
  },
  filterDialog: {
    height: theme.spacing(58),
  },
  dialogContent: {
    width: theme.spacing(46),
    // marginLeft: theme.spacing(-4),
  },
  applyButton: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(1),
    height: theme.spacing(5),
    minWidth: theme.spacing(26),
  },
  headingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {
    flexGrow: 1,
  },
}))

const getTodosToShow = (selected: string, current: TodoFragment[], done: TodoFragment[]) => {
  switch (selected) {
    case 'current':
      return current
    case 'done':
      return done
    default:
      return current
  }
}

export interface WithTodoFragment {
  todo: TodoFragment
}

export interface AccountIdMeta {
  accountId: string
}

export const completedStatuses = [
  TodoItemStatusType.Completed,
  TodoItemStatusType.Expired,
  TodoItemStatusType.Dismissed,
  TodoItemStatusType.Rescheduled,
]

const TodoItemCard: React.FC<WithTodoFragment> = ({ todo }) => {
  switch (todo.type) {
    case 'claim_account':
      return <ClaimAccountTodoCard todo={todo} />
    case 'account_outreach':
      return <AccountOutreachTodoCard todo={todo} />
    default:
      return null
  }
}

enum TodoType {
  AssignOwnership,
  InvestorOutreach,
}

const todoTypes: TodoType[] = [TodoType.AssignOwnership, TodoType.InvestorOutreach]

const todoTypeDisplayName = (todoType: TodoType): string => {
  switch (todoType) {
    case TodoType.AssignOwnership:
      return 'Assign ownership'
    case TodoType.InvestorOutreach:
      return 'Investor outreach'
  }
}

interface RemindersFilterProps {
  hideFilterModal: () => void
  setTodoTypeFilters: (filters: any) => void
  todoTypeFilters: any
}

const RemindersFilter: React.FC<RemindersFilterProps> = ({ hideFilterModal, setTodoTypeFilters, todoTypeFilters }) => {
  const classes = useStyles()
  return (
    <Dialog open maxWidth={false} className={classes.filterDialog}>
      <MuiDialogTitle className={classes.headingContainer}>
        <Typography variant="h6" align="center">
          Filter
        </Typography>
        <span className={classes.spacer} />
        <IconButton aria-label="close" onClick={hideFilterModal}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div>
          <Typography variant="h6">Task type</Typography>
          <Fragment>
            {todoTypes.map(todoType => (
              <div key={todoType}>
                <FormControlLabel
                  key={todoType}
                  label={todoTypeDisplayName(todoType)}
                  control={
                    <Checkbox
                      checked={todoTypeFilters[todoType]}
                      onChange={() =>
                        setTodoTypeFilters({ ...todoTypeFilters, [todoType]: !todoTypeFilters[todoType] })
                      }
                    />
                  }
                />
              </div>
            ))}
          </Fragment>
          <Grid container justify="center">
            <Button variant="contained" color="primary" className={classes.applyButton} onClick={hideFilterModal}>
              Apply
            </Button>
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const Reminders: React.FC = () => {
  const classes = useStyles()
  const buttonClasses = { root: classes.defaultButton, selected: classes.selectedButton }
  const { data } = useAllTodosQuery({ pollInterval: 10000 })
  const allTodos = data?.allTodos ?? []
  const [todoTypeFilters, setTodoTypeFilters] = useState({
    [TodoType.AssignOwnership]: true,
    [TodoType.InvestorOutreach]: true,
  })
  const filteredTodos = allTodos.filter(
    todo =>
      (todo.type === 'claim_account' && todoTypeFilters[TodoType.AssignOwnership]) ||
      (todo.type === 'account_outreach' && todoTypeFilters[TodoType.InvestorOutreach])
  )
  const currentTodos = filteredTodos.filter(todo => todo.status === TodoItemStatusType.Live && !todo.dismissed_at)
  const doneTodosUnordered = filteredTodos.filter(
    todo => completedStatuses.includes(todo.status) || !!todo.dismissed_at
  )
  const doneTodos = doneTodosUnordered.sort((a, b) => (a.completed_at > b.completed_at ? 1 : -1))

  const [selectedColumn, setSelectedColumn] = useState('current')
  const [limit, setLimit] = useState(5)
  const [openFilterModal, hideFilterModal] = useModal(() => {
    return (
      <RemindersFilter
        hideFilterModal={hideFilterModal}
        todoTypeFilters={todoTypeFilters}
        setTodoTypeFilters={setTodoTypeFilters}
      />
    )
  }, [todoTypeFilters])
  const changeType = (evt: any, value: string) => {
    setSelectedColumn(value)
  }
  const showMore = () => {
    setLimit(limit + 5)
  }
  const todosInColumn = getTodosToShow(selectedColumn, currentTodos, doneTodos)
  const todosToShow = todosInColumn.filter((e, index) => {
    return index < limit
  })
  const moreTodosExist = limit < todosInColumn.length
  return (
    <Card>
      <CardHeader title="Reminders" />
      <CardContent>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={selectedColumn}
          onChange={changeType}
          classes={{ root: classes.borderRadius }}>
          <Tooltip title="Filter Reminders" placement="top">
            <ToggleButton
              key={0}
              value="filter"
              onClick={e => {
                openFilterModal()
                e.preventDefault()
              }}
              classes={buttonClasses}>
              <SlidersHIcon style={{ fontSize: 22, position: 'absolute' }} />
            </ToggleButton>
          </Tooltip>
          <ToggleButton key={1} classes={buttonClasses} value="current">
            Current ({currentTodos.length})
          </ToggleButton>
          <ToggleButton key={3} classes={buttonClasses} value="done">
            Done ({doneTodos.length})
          </ToggleButton>
        </ToggleButtonGroup>
        {todosToShow.map(todo => (
          <TodoItemCard key={todo.id} todo={todo} />
        ))}
        {moreTodosExist && (
          <Box mt={2}>
            <SpinnerButton
              variant="contained"
              color="primary"
              onClick={showMore}
              fullWidth
              loading={false}
              caption="Show More"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
